import { Search, ChevronDown } from "lucide-react";
import { useState, Dispatch, SetStateAction } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/use-analytics";
interface SearchFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  cityName: string;
  setCityName: (city: string) => void;
  minPrice: number | undefined;
  setMinPrice: Dispatch<SetStateAction<number | undefined>>;
  maxPrice: number | undefined;
  setMaxPrice: Dispatch<SetStateAction<number | undefined>>;
  radius: number | undefined;
  setRadius: Dispatch<SetStateAction<number | undefined>>;
  handleSearch: () => void;
  placeholderText: string;
}
const SearchFilters = ({
  searchQuery,
  setSearchQuery,
  cityName,
  setCityName,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  radius,
  setRadius,
  handleSearch,
  placeholderText
}: SearchFiltersProps) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAnalytics();
  const handleFilterToggle = (open: boolean) => {
    setIsFiltersOpen(open);
    if (open) {
      trackEvent("Discover", "filter_expand", "Search filters expanded");
    } else {
      trackEvent("Discover", "filter_collapse", "Search filters collapsed");
    }
  };
  const handleSearchClick = () => {
    trackEvent("Discover", "search_button_click", `Query: ${searchQuery || "none"}, City: ${cityName || "none"}, Price: ${minPrice || 0}-${maxPrice || "max"}, Radius: ${radius || "default"}`);
    handleSearch();
  };
  const handleInputChange = (type: string, value: string | number | undefined) => {
    let numValue: number | undefined = undefined;
    if (typeof value === 'string' && value.trim() !== '') {
      numValue = Number(value);
    } else if (typeof value === 'number') {
      numValue = value;
    }
    if (type === 'minPrice') {
      setMinPrice(numValue);
      if (numValue !== undefined) {
        trackEvent("Discover", "min_price_change", `Value: ${numValue}`);
      }
    } else if (type === 'maxPrice') {
      setMaxPrice(numValue);
      if (numValue !== undefined) {
        trackEvent("Discover", "max_price_change", `Value: ${numValue}`);
      }
    } else if (type === 'radius') {
      setRadius(numValue);
      if (numValue !== undefined) {
        trackEvent("Discover", "radius_change", `Value: ${numValue}`);
      }
    }
  };
  return <Collapsible open={isFiltersOpen} onOpenChange={handleFilterToggle} className="border rounded-lg bg-card">
      <CollapsibleTrigger className="flex items-center justify-between w-full p-4 text-left my-0 py-[8px] px-[15px]">
        <span className="font-medium">{t("discover.searchFilters")}</span>
        <ChevronDown className={cn("h-4 w-4 transition-transform duration-200", isFiltersOpen ? "rotate-180" : "")} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="grid gap-4 px-4 pb-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <Label htmlFor="search">{t("discover.search")}</Label>
            <Input id="search" placeholder={placeholderText} value={searchQuery} onChange={e => {
            setSearchQuery(e.target.value);
            if (e.target.value.length > 3) {
              trackEvent("Discover", "search_query_change", `Query: ${e.target.value}`);
            }
          }} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="city">{t("discover.city")}</Label>
            <Input id="city" placeholder={cityName ? "" : t("discover.enterCityName")} value={cityName} onChange={e => {
            setCityName(e.target.value);
            if (e.target.value.length > 2) {
              trackEvent("Discover", "city_change", `City: ${e.target.value}`);
            }
          }} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="price-range">{t("discover.priceRange")}</Label>
            <div className="flex gap-2">
              <Input id="min-price" type="number" placeholder={t("discover.min")} value={minPrice || ''} onChange={e => handleInputChange('minPrice', e.target.value)} />
              <Input id="max-price" type="number" placeholder={t("discover.max")} value={maxPrice || ''} onChange={e => handleInputChange('maxPrice', e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="radius">{t("discover.radius")}</Label>
            <Input id="radius" type="number" placeholder={t("discover.enterRadius")} value={radius || ''} onChange={e => handleInputChange('radius', e.target.value)} />
          </div>
        </div>
        <div className="px-4 pb-4">
          <button onClick={handleSearchClick} className="flex items-center gap-2 px-4 py-2 text-primary-foreground rounded-md transition-colors bg-[#000835]">
            <Search className="h-4 w-4" />
            {t("discover.searchButton")}
          </button>
        </div>
      </CollapsibleContent>
    </Collapsible>;
};
export default SearchFilters;